import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { loginSchema } from "@/schemas/LoginSchema";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { CustomFormField } from "@/components";
import { FormFieldConfig } from "@/types/AppTypes";
import { loginFormFields } from "@/utils/FormFields";
import { LoaderCircle, UserPlus } from "lucide-react";
import { useSignInUser } from "@/hooks";
import { Link } from "react-router-dom";

export default function LoginForm({ className, ...props }: React.ComponentPropsWithoutRef<"div">) {
  const { handleSignIn, loading } = useSignInUser();

  const form = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const renderFormField = (field: FormFieldConfig) => (
    <CustomFormField
      key={field.name || ""}
      control={form.control}
      name={field.name || "unnamed"}
      label={field.label || ""}
      placeholder={field.placeholder}
      type={field.type || "text"}
      className={field.className}
      required={field.required || false}
      disabled={field.disabled || false}
    />
  );

  async function onSubmit(values: z.infer<typeof loginSchema>) {
    try {
      await handleSignIn(values.email, values.password);
    } catch (err) {
      console.error("Login error:", err);
    }
  }

  return (
    <div
      className={cn("flex flex-col h-full w-full gap-6 rounded-lg shadow-sm", className)}
      {...props}
    >
      <Card className="flex flex-col flex-1">
        <CardHeader className="">
          <CardTitle className="text-2xl">Login</CardTitle>
          <CardDescription className="text-palette-text">
            Sign in to your existing account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 ">
              {loginFormFields.map(renderFormField)}

              <Button
                disabled={loading}
                type="submit"
                className="w-full bg-black text-white hover:bg-gray-800 h-11"
              >
                {loading ? (
                  <>
                    <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                    <span>Please wait...</span>
                  </>
                ) : (
                  "Sign In"
                )}
              </Button>

              
              <div className="pt-1  flex  gap-2">
            <Link to="/auth" className="block w-full">
              <Button 
                variant="outline" 
                className="w-full h-11 text-gray-700 hover:text-black hover:bg-gray-100 text-base font-medium"
              >
                <UserPlus size={20} className="mr-2" />
                Sign Up
              </Button>
            </Link>
            <Link to="/forgot-password" className="block w-full">
              <Button 
                variant="outline" 
                className="w-full h-11 text-gray-700 hover:text-black hover:bg-gray-100 text-base font-medium"
              >
                Forgot Password?
              </Button>
            </Link>
          </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
